import type { SearchHit } from "@rhema/client";
import {
  Avatar,
  Button,
  Chip,
  EmptyState,
  ListRow,
  PageHeader,
  SearchField,
  SearchIcon,
  TopBar,
} from "@rhema/ui";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { nameIn } from "../conversation/MessageList";
import { chatTitle, listTime, toneFor } from "../data/view";
import { useClient, useClientState } from "../session/session";
import { chatHref } from "./LaterScreen";

/**
 * Search. Messages are encrypted end to end, so the server cannot search them. This searches the
 * messages this device has already decrypted.
 */
export function SearchScreen({ variant }: { variant: "phone" | "desktop" }) {
  const client = useClient();
  const state = useClientState();
  const [params, setParams] = useSearchParams();
  const chatId = params.get("chat") ?? undefined;
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const chat = chatId ? state.chats[chatId] : undefined;
  const meId = state.me?.id;

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits(null);
      return;
    }
    let stale = false;
    const t = setTimeout(() => {
      void client
        .search(q, chatId ? { chatId } : {})
        .then((found) => {
          if (!stale) setHits(found);
        })
        .catch(() => {
          if (!stale) setHits([]);
        });
    }, 150);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [client, query, chatId]);

  const place = (hit: SearchHit) => {
    const c = state.chats[hit.chatId];
    if (!c) return "";
    return c.kind === "channel" ? `#${c.title}` : chatTitle(c, meId);
  };
  const hrefOf = (hit: SearchHit) => {
    const base = chatHref(state.chats[hit.chatId], hit.chatId, state);
    const root = hit.message.threadRoot;
    return root && state.chats[hit.chatId]?.kind === "channel" ? `${base}?thread=${root}` : base;
  };
  const scopeTitle = chat ? (chat.kind === "channel" ? `#${chat.title}` : chatTitle(chat, meId)) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-linen">
      {variant === "phone" ? (
        <TopBar
          back={{ href: chat ? chatHref(chat, chat.id, state) : "/chats", label: "Back" }}
          title="Search"
        />
      ) : (
        <PageHeader title="Search" size="md" />
      )}
      <div className="flex flex-none flex-col gap-2.5 px-4 pb-3 min-[900px]:px-6">
        <SearchField
          label="Search messages"
          placeholder={scopeTitle ? `Search in ${scopeTitle}` : "Search your messages"}
          value={query}
          onChange={(v) => {
            setQuery(v);
            const p = new URLSearchParams(params);
            if (v) p.set("q", v);
            else p.delete("q");
            setParams(p, { replace: true });
          }}
          autoFocus
        />
        {scopeTitle && (
          <div className="flex items-center gap-2">
            <Chip pressed size="sm">
              In {scopeTitle}
            </Chip>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const p = new URLSearchParams(params);
                p.delete("chat");
                setParams(p, { replace: true });
              }}
            >
              Search every chat
            </Button>
          </div>
        )}
        <p className="m-0 text-[12.5px] text-ink-meta">
          Messages are encrypted, so search looks through the messages on this device.
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-6">
        {hits === null ? (
          <EmptyState icon={<SearchIcon size={26} />} title="Search your messages" className="py-12">
            Type a word or two from a message, a caption or a file name.
          </EmptyState>
        ) : hits.length === 0 ? (
          <EmptyState title="No messages found" className="py-12">
            Nothing on this device matches. Older messages you have not opened here are not searched.
          </EmptyState>
        ) : (
          <ul aria-label="Results" className="m-0 list-none p-0">
            {hits.map((hit) => {
              const c = state.chats[hit.chatId];
              const sender = c ? nameIn(c, hit.message.senderId, meId) : "Someone";
              return (
                <li key={`${hit.chatId}:${hit.message.id}`}>
                  <ListRow
                    href={hrefOf(hit)}
                    leading={<Avatar name={sender} tone={toneFor(hit.message.senderId ?? "")} size={40} />}
                    title={
                      <span className="flex items-baseline gap-2">
                        <span className="truncate">{sender}</span>
                        <span className="truncate text-[13px] font-normal text-ink-secondary">
                          {place(hit)}
                        </span>
                      </span>
                    }
                    subtitle={<span className="truncate">{hit.text}</span>}
                    trailing={
                      <span className="flex-none text-xs text-ink-meta">
                        {listTime(hit.message.createdAt)}
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
